import { getBackgroundColour, getTextColour } from "@helpers/colours";
import { prettyMoney, uglyMoney } from "@helpers/currency";
import { UIDateFormat } from "@helpers/dates";
import { getOptionTotal, getOptionValue } from "@helpers/options";
import currency from "currency.js";
import moment from "moment";
import React from "react";
import {
  DateInput,
  DateTimeInput,
  TimeInput,
} from "semantic-ui-calendar-react";
import {
  Checkbox,
  Dropdown,
  Form,
  Icon,
  Input,
  Label,
  TextArea,
} from "semantic-ui-react";
import TextInput from "./TextInput";

class Option extends React.Component {
  state = {
    focusValue: null,
  };

  handleListChange = (value) => {
    const selection = this.props.option.data.find((x) => x.id === value);
    const option = { ...this.props.option, defaultList: { ...selection } };
    this.props.onChange(option, value);
  };

  handleMoneyChange = (value) => {
    const isValid = /^[\d.]+$/.test(value) || value.length < 1;

    if (isValid) {
      this.handleTextChange(value);
    }
  };

  handleTextChange = async (
    value,
    updateAttribute = "defaultText",
    reallyDoRefresh = false,
  ) => {
    const { option } = this.props;
    let update = true;
    let isValid = true;

    if (value.length > option.maxLength && option.maxLength > 0) {
      return;
    }

    if (update && option.entryPattern && value.length > 0) {
      const { allowedCharacterRegex, appends } = option.entryPattern;
      const regex = new RegExp(allowedCharacterRegex);
      const lastCharacter = value.slice(-1);

      if (getOptionValue(option).length < value.length) {
        update = regex.test(lastCharacter) || lastCharacter === "-";
        const appendedCharacter = appends.find(
          (x) => x.position === value.length,
        );
        if (appendedCharacter) {
          value += appendedCharacter.text;
        }
      }
    }

    if (update) {
      if (option.regex && value.length) {
        const regex = new RegExp(option.regex);
        isValid = regex.test(value);
      }
      if (isValid && option.checkDenom > 0) {
        isValid = (currency(value) * 1000) % (option.checkDenom * 1000) === 0;
      }
      this.props.onChange(
        { ...this.props.option, isValid, [updateAttribute]: value },
        value,
        "changed",
        reallyDoRefresh,
      );
    }
  };

  handleCheckboxChange = (option) => {
    const value =
      option.defaultText.toLowerCase() === "true" ? "false" : "true";
    this.props.onChange({ ...option, defaultText: value }, value);
  };

  renderError = () =>
    this.props.option.isValid === false && (
      <small class="optionError">Invalid {this.props.option.text}</small>
    );

  handleMoneyBlur = (event) => {
    const { value } = event.target;

    if (isNaN(value)) {
      return;
    }

    const valueChangedSinceFocus =
      uglyMoney(value) !== uglyMoney(this.state.focusValue);
    this.props.onChange(
      { ...this.props.option, isValid: true, defaultText: prettyMoney(value) },
      prettyMoney(value),
      "changed",
      valueChangedSinceFocus,
    );
  };

  handleMoneyFocus = async (event) => {
    event.persist();
    const { value } = event.target;
    this.setState({ focusValue: value }, () => {
      this.props
        .onChange(
          {
            ...this.props.option,
            isValid: true,
            defaultText: uglyMoney(value),
          },
          uglyMoney(value),
          "changed",
          false,
        )
        .then(() => {
          event.target.select();
        });
    });
  };

  static getIcon = (option) => {
    switch (option.currencySymbol) {
      case "£":
        return "pound";
      case "€":
        return "euro";
      case "$":
        return "dollar";
      default:
        return "pound";
    }
  };

  render() {
    const { option } = this.props;
    const error = option.isValid === false;

    // @todo: base set of props to pass to each of these
    // @todo: split these into separate components

    switch (option.type) {
      case 1: // List (tested)
        const options = option.data.length
          ? option.data.map((option, index) => {
              return {
                ...option,
                value: option.id,
                key: `${option.text} ${index}`,
              };
            })
          : [];
        return (
          <Dropdown
            selection
            options={options}
            value={getOptionValue(option)}
            onChange={(event, { value }) => this.handleListChange(value)}
            tabIndex={this.props.tabIndex}
            disabled={!option.isEditable}
            placeholder={option.placeHolder}
            fluid={!this.props.inline}
            error={error}
            onFocus={() => this.props.onFocus(option.code)}
            focus={option.code === this.props.focus}
            autoFocus={option.code === this.props.focus || option.setFocus}
          />
        );

      case 2: // Text
        return (
          <Form.Field error={error}>
            <TextInput
              option={option}
              onChange={this.handleTextChange}
              tabIndex={this.props.tabIndex}
              error={error}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
              onBlur={() =>
                this.handleTextChange(
                  getOptionValue(option),
                  "defaultText",
                  true,
                )
              }
            />
            {this.renderError()}
          </Form.Field>
        );

      case 3: // Date
        return (
          <>
            <DateInput
              name="startDate"
              key={option.code}
              value={moment(getOptionValue(option))}
              iconPosition="left"
              onChange={(event, { value }) =>
                this.handleTextChange(
                  moment(value, UIDateFormat).toISOString(),
                  "defaultDate",
                  true,
                )
              }
              readOnly={!option.isEditable}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
              animation="fade"
              tabIndex={this.props.tabIndex}
              popupPosition="bottom center"
              closable
              placeholder={option.placeHolder}
              autoComplete="off"
              error={error}
            />
            {this.renderError()}
          </>
        );

      case 4: // Multi
        return <h1>Multi</h1>;

      case 5: // Datetime
        return (
          <Form.Field error={error}>
            <DateTimeInput
              name="startDate"
              fluid
              value={getOptionValue(option)}
              iconPosition="left"
              onChange={(event, { value }) => this.handleTextChange(value)}
              readOnly={!option.isEditable}
              tabIndex={this.props.tabIndex}
              animation="fade"
              popupPosition="bottom center"
              closable
              autoComplete="off"
              error={error}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
            />
            {this.renderError()}
          </Form.Field>
        );
      case 6: // Money
        // @todo: if we swap this out for a label, and swap the label to an input on focus, we can handle the formatting without all this complexity for refreshes.
        return (
          <Form.Field error={error}>
            <Input
              fluid
              icon={Option.getIcon(option)}
              iconPosition="left"
              type="text"
              onFocus={this.handleMoneyFocus}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
              onChange={(event, { value }) => this.handleMoneyChange(value)}
              onBlur={this.handleMoneyBlur}
              value={getOptionValue(option)}
              tabIndex={this.props.tabIndex}
              readOnly={!option.isEditable}
              disabled={!option.isEditable}
              error={error}
            />
            {this.renderError()}
          </Form.Field>
        );

      case 7: // Int
        return (
          <Form.Field error={error}>
            <Input
              fluid
              value={getOptionValue(option)}
              onChange={(event, { value }) => this.handleTextChange(value)}
              tabIndex={this.props.tabIndex}
              readOnly={!option.isEditable}
              type="number"
              error={error}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
              onBlur={() =>
                this.handleTextChange(
                  getOptionValue(option),
                  "defaultText",
                  true,
                )
              }
            />
            {this.renderError()}
          </Form.Field>
        );

      case 8: // switch
      case 10: // checkbox
        return (
          <Checkbox
            error={error}
            label={option.type === 8 ? null : option.text}
            toggle={option.type === 8}
            checked={getOptionValue(option).toLowerCase() === "true"}
            onChange={() => this.handleCheckboxChange(option)}
            disabled={!option.isEditable}
          />
        );

      case 9:
        const background = getBackgroundColour(option);
        const color = getTextColour(option);
        return background !== "transparent" ? (
          <Label
            style={{
              background,
              color,
              //  float: "right",
              "min-width": "90px",
              "text-align": "center",
            }}
          >
            {option.defaultText}
          </Label>
        ) : (
          <span>{option.defaultText}</span>
        );

      case 11:
        return (
          <Form.Field error={error}>
            <TextArea
              onChange={(event, { value }) => this.handleTextChange(value)}
              className="w-100"
              disabled={!option.isEditable}
              fluid
              rows={4}
              value={option.defaultText}
              tabIndex={this.props.tabIndex}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
            />
          </Form.Field>
        );

      case 12:
        return <Icon name={option.defaultText} />;

      case 13:
        const total = getOptionTotal(option, this.props.optionsList);
        const processedTotal = option.isCurrency
          ? `${option.currencySymbol}${parseFloat(total).toFixed(2) || 0}${option.suffix}`
          : `${parseInt(total)}${option.suffix}` || 0;
        return (
          <span style={{ color: total <= option.maxValue ? "black" : "red" }}>
            {processedTotal}
          </span>
        );

      case 14: // Percentage
        return (
          <Form.Field error={error}>
            <Input
              fluid
              value={getOptionValue(option)}
              onChange={(event, { value }) => this.handleTextChange(value)}
              label="%"
              labelPosition="right"
              tabIndex={this.props.tabIndex}
              readOnly={!option.isEditable}
              type="number"
              error={error}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
            />
            {this.renderError()}
          </Form.Field>
        );

      case 15: // Time
        return (
          <Form.Field error={error} width={16}>
            <TimeInput
              fluid
              value={getOptionValue(option)}
              iconPosition="left"
              onChange={(event, { value }) => this.handleTextChange(value)}
              readOnly={!option.isEditable}
              tabIndex={this.props.tabIndex}
              animation="fade"
              popupPosition="bottom center"
              closable
              autoComplete="off"
              error={error}
              onFocus={() => this.props.onFocus(option.code)}
              focus={option.code === this.props.focus}
              autoFocus={option.code === this.props.focus || option.setFocus}
            />
            {this.renderError()}
          </Form.Field>
        );

      default:
        return <span>{option.defaultText}</span>;
    }
  }
}

export default Option;
